import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MeditationSessionPhase,
  meditationSessionProgress,
  totalMeditationDurationSeconds,
} from './meditationSession';
import {
  releaseMeditationWakeLock,
  requestMeditationWakeLock,
  subscribeToMeditationAppState,
} from '../../platform/meditationRuntime';

/**
 * State machine of the guided meditation session (MI-003).
 *
 * Elapsed time is read from timestamp differences, never from accumulated
 * setInterval ticks, so app suspensions and tab switches cannot drift the
 * session away from the content. The interval only drives re-rendering.
 *
 * onPhaseStart / onPhaseEnd / onComplete are the seam narration (MI-011) and
 * haptics plug into later, without touching this machine.
 */

export type MeditationSessionStatus = 'idle' | 'running' | 'paused' | 'finished';

export interface MeditationSessionCallbacks {
  onPhaseStart?: (phase: MeditationSessionPhase, index: number) => void;
  onPhaseEnd?: (phase: MeditationSessionPhase, index: number) => void;
  onComplete?: () => void;
}

export interface MeditationSession {
  status: MeditationSessionStatus;
  phases: readonly MeditationSessionPhase[];
  phaseIndex: number;
  currentPhase: MeditationSessionPhase | null;
  phaseElapsedSeconds: number;
  /** Elapsed time of the current phase in milliseconds, for animations that need sub second detail. */
  phaseElapsedMs: number;
  /** Null while the current phase is self paced. */
  phaseRemainingSeconds: number | null;
  phaseProgress: number;
  sessionProgress: number;
  /** Null when at least one phase is self paced. */
  totalDurationSeconds: number | null;
  isActive: boolean;
  wakeLockUnavailable: boolean;
  start: () => void;
  pause: () => void;
  resume: () => void;
  skipPhase: () => void;
  stop: () => void;
}

const TICK_INTERVAL_MS = 200;
export const LONG_BACKGROUND_ABSENCE_MS = 60_000;

const clamp01 = (value: number): number => Math.min(Math.max(value, 0), 1);

export const useMeditationSession = (
  phases: readonly MeditationSessionPhase[],
  callbacks: MeditationSessionCallbacks = {},
): MeditationSession => {
  const [status, setStatus] = useState<MeditationSessionStatus>('idle');
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseElapsedMs, setPhaseElapsedMs] = useState(0);
  const [wakeLockUnavailable, setWakeLockUnavailable] = useState(false);
  const [wakeLockRequestVersion, setWakeLockRequestVersion] = useState(0);

  // Timestamp the current running segment started at, or null while not running.
  const segmentStartRef = useRef<number | null>(null);
  // Milliseconds already spent in the current phase, before the running segment.
  const carriedMsRef = useRef(0);
  const phaseIndexRef = useRef(0);
  const statusRef = useRef<MeditationSessionStatus>('idle');
  const backgroundStartedAtRef = useRef<number | null>(null);
  const phasesRef = useRef(phases);
  const callbacksRef = useRef(callbacks);

  phasesRef.current = phases;
  callbacksRef.current = callbacks;
  statusRef.current = status;

  const readElapsedMs = useCallback((readAt: number = Date.now()) => {
    const segmentStart = segmentStartRef.current;
    const runningMs = segmentStart === null ? 0 : Math.max(0, readAt - segmentStart);

    return carriedMsRef.current + runningMs;
  }, []);

  const enterPhase = useCallback((index: number, running: boolean) => {
    phaseIndexRef.current = index;
    carriedMsRef.current = 0;
    segmentStartRef.current = running ? Date.now() : null;
    setPhaseIndex(index);
    setPhaseElapsedMs(0);

    const phase = phasesRef.current[index];

    if (phase) {
      callbacksRef.current.onPhaseStart?.(phase, index);
    }
  }, []);

  const endCurrentPhase = useCallback(() => {
    const index = phaseIndexRef.current;
    const phase = phasesRef.current[index];

    if (phase) {
      callbacksRef.current.onPhaseEnd?.(phase, index);
    }

    return index;
  }, []);

  const finishSession = useCallback(() => {
    segmentStartRef.current = null;
    carriedMsRef.current = 0;
    backgroundStartedAtRef.current = null;
    setPhaseElapsedMs(0);
    setStatus('finished');
    statusRef.current = 'finished';
    callbacksRef.current.onComplete?.();
  }, []);

  const goToNextPhase = useCallback(() => {
    const index = endCurrentPhase();
    const nextIndex = index + 1;

    if (nextIndex >= phasesRef.current.length) {
      finishSession();
      return;
    }

    enterPhase(nextIndex, statusRef.current === 'running');
  }, [endCurrentPhase, enterPhase, finishSession]);

  const start = useCallback(() => {
    if (phasesRef.current.length === 0) {
      return;
    }

    setStatus('running');
    statusRef.current = 'running';
    backgroundStartedAtRef.current = null;
    enterPhase(0, true);
  }, [enterPhase]);

  const pause = useCallback(() => {
    if (statusRef.current !== 'running') {
      return;
    }

    carriedMsRef.current = readElapsedMs();
    segmentStartRef.current = null;
    setPhaseElapsedMs(carriedMsRef.current);
    setStatus('paused');
    statusRef.current = 'paused';
    backgroundStartedAtRef.current = null;
  }, [readElapsedMs]);

  const resume = useCallback(() => {
    if (statusRef.current !== 'paused') {
      return;
    }

    segmentStartRef.current = Date.now();
    setStatus('running');
    statusRef.current = 'running';
  }, []);

  const skipPhase = useCallback(() => {
    if (statusRef.current !== 'running' && statusRef.current !== 'paused') {
      return;
    }

    goToNextPhase();
  }, [goToNextPhase]);

  const stop = useCallback(() => {
    if (statusRef.current === 'running' || statusRef.current === 'paused') {
      endCurrentPhase();
    }

    segmentStartRef.current = null;
    carriedMsRef.current = 0;
    backgroundStartedAtRef.current = null;
    phaseIndexRef.current = 0;
    setPhaseIndex(0);
    setPhaseElapsedMs(0);
    setStatus('idle');
    statusRef.current = 'idle';
  }, [endCurrentPhase]);

  const suspendForBackground = useCallback((suspendedAt: number) => {
    if (statusRef.current !== 'running' || backgroundStartedAtRef.current !== null) {
      return;
    }

    carriedMsRef.current = readElapsedMs(suspendedAt);
    segmentStartRef.current = null;
    backgroundStartedAtRef.current = suspendedAt;
    setPhaseElapsedMs(carriedMsRef.current);
  }, [readElapsedMs]);

  const resumeFromBackground = useCallback((resumedAt: number): boolean => {
    const backgroundStartedAt = backgroundStartedAtRef.current;
    backgroundStartedAtRef.current = null;

    if (backgroundStartedAt === null || statusRef.current !== 'running') {
      return false;
    }

    const absenceMs = Math.max(0, resumedAt - backgroundStartedAt);

    if (absenceMs > LONG_BACKGROUND_ABSENCE_MS) {
      segmentStartRef.current = null;
      setPhaseElapsedMs(carriedMsRef.current);
      setStatus('paused');
      statusRef.current = 'paused';
      return false;
    }

    let nextIndex = phaseIndexRef.current;
    let nextElapsedMs = carriedMsRef.current + absenceMs;

    while (nextIndex < phasesRef.current.length) {
      const phase = phasesRef.current[nextIndex];
      const durationMs = phase.durationSeconds === null ? null : phase.durationSeconds * 1000;

      if (durationMs === null || nextElapsedMs < durationMs) {
        break;
      }

      callbacksRef.current.onPhaseEnd?.(phase, nextIndex);
      nextElapsedMs -= durationMs;
      nextIndex += 1;

      if (nextIndex >= phasesRef.current.length) {
        finishSession();
        return false;
      }

      callbacksRef.current.onPhaseStart?.(phasesRef.current[nextIndex], nextIndex);
    }

    phaseIndexRef.current = nextIndex;
    carriedMsRef.current = nextElapsedMs;
    segmentStartRef.current = resumedAt;
    setPhaseIndex(nextIndex);
    setPhaseElapsedMs(nextElapsedMs);
    return true;
  }, [finishSession]);

  useEffect(() => subscribeToMeditationAppState((isActive, changedAt) => {
    if (!isActive) {
      suspendForBackground(changedAt);
      void releaseMeditationWakeLock();
      return;
    }

    if (resumeFromBackground(changedAt)) {
      setWakeLockRequestVersion((version) => version + 1);
    }
  }), [resumeFromBackground, suspendForBackground]);

  useEffect(() => {
    let disposed = false;

    if (status !== 'running') {
      void releaseMeditationWakeLock();
      return undefined;
    }

    void requestMeditationWakeLock().then((acquired) => {
      if (disposed) {
        if (acquired) {
          void releaseMeditationWakeLock();
        }
        return;
      }

      if (!acquired) {
        setWakeLockUnavailable(true);
      }
    });

    return () => {
      disposed = true;
      void releaseMeditationWakeLock();
    };
  }, [status, wakeLockRequestVersion]);

  useEffect(() => {
    if (status !== 'running') {
      return;
    }

    const intervalId = setInterval(() => {
      const elapsedMs = readElapsedMs();
      const phase = phasesRef.current[phaseIndexRef.current];
      const durationSeconds = phase?.durationSeconds ?? null;

      if (durationSeconds !== null && elapsedMs >= durationSeconds * 1000) {
        goToNextPhase();

        return;
      }

      setPhaseElapsedMs(elapsedMs);
    }, TICK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [status, readElapsedMs, goToNextPhase]);

  const currentPhase = phases[phaseIndex] ?? null;
  const currentDurationSeconds = currentPhase?.durationSeconds ?? null;

  const phaseProgress =
    currentDurationSeconds === null || currentDurationSeconds === 0
      ? 0
      : clamp01(phaseElapsedMs / (currentDurationSeconds * 1000));

  const phaseElapsedSeconds =
    currentDurationSeconds === null
      ? Math.floor(phaseElapsedMs / 1000)
      : Math.min(currentDurationSeconds, Math.floor(phaseElapsedMs / 1000));

  const phaseRemainingSeconds =
    currentDurationSeconds === null ? null : Math.max(0, currentDurationSeconds - phaseElapsedSeconds);

  const boundedPhaseElapsedMs =
    currentDurationSeconds === null
      ? phaseElapsedMs
      : Math.min(currentDurationSeconds * 1000, phaseElapsedMs);

  const totalDurationSeconds = useMemo(() => totalMeditationDurationSeconds(phases), [phases]);

  const sessionProgress = useMemo(() => {
    if (status === 'finished') {
      return 1;
    }

    if (status === 'idle') {
      return 0;
    }

    return meditationSessionProgress(phases, phaseIndex, phaseProgress);
  }, [phases, phaseIndex, phaseProgress, status]);

  return {
    status,
    phases,
    phaseIndex,
    currentPhase,
    phaseElapsedSeconds,
    phaseElapsedMs: boundedPhaseElapsedMs,
    phaseRemainingSeconds,
    phaseProgress,
    sessionProgress,
    totalDurationSeconds,
    isActive: status === 'running' || status === 'paused',
    wakeLockUnavailable,
    start,
    pause,
    resume,
    skipPhase,
    stop,
  };
};

export default useMeditationSession;
